import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, RefreshCw } from 'lucide-react';

export interface ProgramOption {
  id: string;
  label: string;
  description?: string;
  program_price: number;
  clinic_facilitation_fee: number;
  includes_services?: string[];
  is_subscription: boolean;
  subscription_frequency?: 'weekly' | 'monthly' | 'quarterly';
  sort_order: number;
}

interface ProtocolOptionCardProps {
  option: ProgramOption;
  programTitle: string;
  programId: string;
  isSelected: boolean;
  onSelect: (option: ProgramOption) => void;
  /**
   * Label shown on the in-card action button.
   * Pass "Select" for staff and "Get Started" for patients.
   * Defaults to "Select This Option" when omitted (backward-compatible).
   */
  ctaLabel?: string;
}

const formatCurrency = (cents: number) => {
  return `$${(cents / 100).toFixed(2)}`;
};

const frequencyLabel: Record<string, string> = {
  weekly: '/week',
  monthly: '/month',
  quarterly: '/quarter',
};

const ProtocolOptionCard: React.FC<ProtocolOptionCardProps> = ({
  option,
  programTitle,
  programId,
  isSelected,
  onSelect,
  ctaLabel,
}) => {
  const buttonLabel = isSelected ? 'Selected' : (ctaLabel ?? 'Select This Option');
  return (
    <Card
      className={`relative cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected
          ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/30'
          : 'border-slate-200 hover:border-slate-300'
      }`}
      onClick={() => onSelect(option)}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-3">
            {/* Option label */}
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-slate-900">{option.label}</h3>
              {option.is_subscription ? (
                <Badge variant="outline" className="text-xs border-blue-200 text-blue-700 bg-blue-50">
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Recurring
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs border-emerald-200 text-emerald-700 bg-emerald-50">
                  One-Time
                </Badge>
              )}
            </div>

            {/* Description */}
            {option.description && (
              <p className="text-sm text-slate-600 leading-relaxed">{option.description}</p>
            )}

            {/* Included services */}
            {option.includes_services && option.includes_services.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Includes</p>
                <ul className="space-y-1">
                  {option.includes_services.map((service, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Price */}
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-bold text-slate-900">
              {formatCurrency(option.program_price)}
            </p>
            {option.is_subscription && option.subscription_frequency ? (
              <p className="text-sm text-slate-500">
                {frequencyLabel[option.subscription_frequency] || ''}
              </p>
            ) : (
              <p className="text-sm text-slate-500">one-time payment</p>
            )}
            <p className="text-xs text-slate-400 mt-1">Consulting Fee</p>
          </div>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-3 right-3">
            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          </div>
        )}

        {/* Select / CTA button */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <Button
            variant={isSelected ? 'default' : 'outline'}
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(option);
            }}
          >
            {buttonLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProtocolOptionCard;
